import React, { useCallback, useEffect, useState } from 'react';
import { Shimmer } from 'ggApp/shared/components/shimmerEffect/styles';
import PropTypes from 'prop-types';
import isFunction from 'ggApp/utils/function/isFunction';
import useMount from 'ggApp/shared/hooks/useMount';
import VimeoPlayer from '@vimeo/player';
import { VideoContent, VideoPanel, Play, Loader, Title, Pause } from './styles';

const DEFAULT_OPTIONS = {
    responsive: true,
    portrait: false,
    controls: false,
    dnt: true,
    loop: true,
    preload: true,
};

const VimeoEmbed = ({
    playerId,
    id,
    options: { title },
    onPlay,
    onPause,
    onError,
    controlsPosition,
}) => {
    const [player, setPlayer] = useState(null);
    const [videoActive, setVideoActive] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);
    const [videoLoading, setVideoLoading] = useState(false);
    const [invalidId, setInvalidId] = useState(false);
    const [videoTouched, setVideoTouched] = useState(false);

    const containerId = `${playerId}${id}`;

    const play = useCallback(
        (e) => {
            if (e) {
                e.stopPropagation();
                e.preventDefault();
            }
            if (!videoTouched) {
                setVideoTouched(true);
                setVideoLoading(true);
            }

            player
                .play()
                .then(() => {
                    setVideoActive(true);
                    setVideoLoading(false);

                    if (isFunction(onPlay)) {
                        onPlay();
                    }
                })
                .catch(() => {
                    // eslint-disable-next-line no-console
                    console.warn('Video can not be played on your device.');
                });
        },
        [player, onPlay, videoTouched],
    );

    const pause = useCallback(
        (e) => {
            if (e) {
                e.stopPropagation();
                e.preventDefault();
            }
            player.pause().then(() => {
                setVideoActive(false);
            });

            if (isFunction(onPause)) {
                onPause();
            }
        },
        [player, onPause],
    );

    useMount(() => {
        if (!/^[0-9]*$/.test(`${id}`)) {
            setInvalidId(true);
            return;
        }
        let instance;

        try {
            instance = new VimeoPlayer(containerId, {
                ...DEFAULT_OPTIONS,
                id,
            });
        } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Something crashed during vimeo initialization');
        }

        instance
            .ready()
            .then(() => {
                setVideoLoaded(true);
            })
            .catch((error) => {
                if (isFunction(onError)) {
                    onError(error);
                }
                // eslint-disable-next-line no-console
                console.warn(error);
            });

        setPlayer(instance);
    });

    useEffect(() => {
        if (player) {
            player
                .loadVideo(id)
                .then(() => {
                    setVideoLoaded(true);
                })
                .catch((error) => {
                    if (isFunction(onError)) {
                        onError(error);
                    }
                    // eslint-disable-next-line no-console
                    console.warn(error);
                });
        }

        return () => player && player.unload();
    }, [player, id, onError]);

    return (
        <>
            <Shimmer loading={!videoLoaded || invalidId} />
            {videoLoaded && (
                <VideoPanel onClick={videoActive ? pause : play}>
                    {title && !videoActive && <Title>{title}</Title>}
                    {!videoActive && <Play onClick={play} controlsPosition={controlsPosition} />}
                    {videoActive && <Pause controlsPosition={controlsPosition} />}
                    {videoLoading && <Loader />}
                </VideoPanel>
            )}
            <VideoContent id={containerId} loading={!videoLoaded} />
        </>
    );
};

VimeoEmbed.propTypes = {
    options: PropTypes.shape({
        preload: PropTypes.bool, // if true, video shouldn't have black background on 0.0 seconds
        title: PropTypes.string,
    }),
    onPlay: PropTypes.func,
    onPause: PropTypes.func,
    onError: PropTypes.func,
    playerId: PropTypes.string,
    controlsPosition: PropTypes.string,
};

VimeoEmbed.defaultProps = {
    options: {},
    onPlay: () => null,
    onPause: () => null,
    onError: () => null,
    playerId: 'pdp-product-video-',
    controlsPosition: 'bottomLeft',
};

export default VimeoEmbed;
